import {
	BarChart3,
	ClipboardList,
	FileText,
	Globe,
	LogOut,
	Menu,
	Settings as SettingsIcon,
	Users,
	X,
} from "lucide-react";
import type React from "react";
import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { ThemeToggle, useTheme } from "../context/ThemeContext";
import { cn } from "../lib/utils";
import { Avatar, AvatarFallback } from "./ui/avatar";

const Header: React.FC = () => {
	const {
		isAuthenticated,
		isAdmin,
		hasAdminAccess,
		isStellarAdmin,
		user,
		logout,
	} = useAuth();
	const { theme } = useTheme();
	const navigate = useNavigate();
	const location = useLocation();
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

	const handleLogout = async () => {
		await logout();
		navigate("/login");
		setMobileMenuOpen(false);
	};

	if (!isAuthenticated) {
		return null;
	}

	const getInitials = (name?: string, email?: string) => {
		if (name?.trim()) {
			const parts = name.trim().split(" ");
			if (parts.length >= 2) {
				return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
			}
			return name.substring(0, 2).toUpperCase();
		}
		if (email?.trim()) {
			return email.split("@")[0].substring(0, 2).toUpperCase();
		}
		return "?";
	};

	const getRoleLabel = () => {
		if (isAdmin) return "Admin";
		if (isStellarAdmin) return "Stellar Admin";
		return "Partner";
	};

	const isActive = (path: string) => location.pathname.startsWith(path);

	const navLinkClass = (path: string) =>
		cn(
			"relative flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-all duration-150",
			isActive(path)
				? "text-(--color-accent) bg-(--color-accent-muted)"
				: "text-(--color-muted-foreground) hover:text-(--color-foreground) hover:bg-(--color-muted)",
		);

	const mobileNavLinkClass = (path: string) =>
		cn(
			"flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-150 w-full",
			isActive(path)
				? "text-(--color-accent) bg-(--color-accent-muted)"
				: "text-(--color-muted-foreground) hover:text-(--color-foreground) hover:bg-(--color-muted)",
		);

	const navItems = [
		{
			path: "/applications",
			label: "Onboarding",
			icon: <ClipboardList size={15} />,
		},
		{ path: "/kyc", label: "KYB Applications", icon: <FileText size={15} /> },
		{ path: "/users", label: "Users", icon: <Users size={15} /> },
		{
			path: "/settlement-analysis",
			label: "Settlement",
			icon: <BarChart3 size={15} />,
		},
		{ path: "/stellar-admin", label: "Stellar", icon: <Globe size={15} /> },
		{ path: "/settings", label: "Settings", icon: <SettingsIcon size={15} /> },
	];

	const stellarAdminNavItems = [
		{ path: "/kyc", label: "KYB Applications", icon: <FileText size={15} /> },
		{ path: "/stellar-admin", label: "Stellar", icon: <Globe size={15} /> },
	];

	const activeNavItems = isAdmin ? navItems : stellarAdminNavItems;

	return (
		<header className="fixed top-0 left-0 right-0 z-[100] border-b border-(--color-border) bg-(--color-background)/95 backdrop-blur-md">
			<div className="mx-auto flex max-w-[1400px] items-center justify-between px-6 h-14">
				{/* Logo */}
				<Link to="/" className="flex items-center shrink-0 mr-6">
					<img
						src={theme === "dark" ? "/logo-dark.svg" : "/logo.svg"}
						alt="MoneyGram"
						className="h-7 w-auto"
					/>
				</Link>

				{/* Desktop Nav */}
				{hasAdminAccess && (
					<nav className="hidden md:flex items-center gap-0.5 flex-1">
						{activeNavItems.map(({ path, label, icon }) => (
							<Link key={path} to={path} className={navLinkClass(path)}>
								<span
									className={cn(
										"transition-colors",
										isActive(path)
											? "text-(--color-accent)"
											: "text-[#9a9a9a] dark:text-gray-400",
									)}
								>
									{icon}
								</span>
								{label}
							</Link>
						))}
					</nav>
				)}

				{/* Right side */}
				<div className="hidden md:flex items-center gap-2 ml-auto">
					<ThemeToggle />

					{/* Divider */}
					<div className="w-px h-5 bg-(--color-border) mx-1" />

					{/* User pill */}
					<div className="flex items-center gap-2.5 pl-1">
						<Avatar className="h-8 w-8 ring-1 ring-[#E5E5E5] dark:ring-white/10">
							<AvatarFallback className="bg-(--color-accent-muted) text-(--color-accent) font-semibold text-xs">
								{getInitials(user?.name, user?.email)}
							</AvatarFallback>
						</Avatar>
						<div className="hidden lg:block leading-tight">
							<p className="text-sm font-semibold text-(--color-foreground)">
								{user?.name || user?.email?.split("@")[0]}
							</p>
							<p className="text-[11px] text-[#9a9a9a] dark:text-gray-400 uppercase tracking-wide font-medium">
								{getRoleLabel()}
							</p>
						</div>
					</div>

					<button
						type="button"
						onClick={handleLogout}
						title="Logout"
						className="ml-1 flex items-center justify-center w-8 h-8 rounded-lg text-[#9a9a9a] dark:text-gray-400 hover:text-(--color-accent) hover:bg-(--color-muted) transition-colors"
					>
						<LogOut size={16} />
					</button>
				</div>

				{/* Mobile Menu Button */}
				<button
					type="button"
					className="md:hidden flex items-center justify-center w-8 h-8 rounded-lg text-(--color-muted-foreground) hover:bg-(--color-muted) transition-colors"
					onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
					aria-label="Toggle menu"
				>
					{mobileMenuOpen ? <X size={18} /> : <Menu size={18} />}
				</button>
			</div>

			{/* Mobile Nav */}
			{mobileMenuOpen && (
				<>
					<div
						className="fixed inset-0 z-30 bg-black/40 md:hidden"
						onClick={() => setMobileMenuOpen(false)}
					/>
					<nav className="fixed top-14 right-0 z-40 w-64 h-[calc(100vh-3.5rem)] bg-(--color-background) border-l border-(--color-border) shadow-2xl overflow-y-auto md:hidden">
						<div className="p-3">
							{/* User info */}
							<div className="flex items-center gap-3 px-3 py-3 mb-3 rounded-xl bg-(--color-muted)">
								<Avatar className="h-9 w-9 ring-1 ring-[#E5E5E5] dark:ring-white/10 shrink-0">
									<AvatarFallback className="bg-(--color-accent-muted) text-(--color-accent) font-semibold text-xs">
										{getInitials(user?.name, user?.email)}
									</AvatarFallback>
								</Avatar>
								<div className="flex-1 min-w-0">
									<p className="text-sm font-semibold text-(--color-foreground) truncate">
										{user?.name || user?.email?.split("@")[0]}
									</p>
									<p className="text-[11px] text-[#9a9a9a] dark:text-gray-400 uppercase tracking-wide font-medium">
										{getRoleLabel()}
									</p>
								</div>
							</div>

							{/* Nav links */}
							{hasAdminAccess && (
								<div className="space-y-0.5 mb-3">
									{activeNavItems.map(({ path, label, icon }) => (
										<Link
											key={path}
											to={path}
											className={mobileNavLinkClass(path)}
											onClick={() => setMobileMenuOpen(false)}
										>
											<span
												className={cn(
													"transition-colors",
													isActive(path)
														? "text-(--color-accent)"
														: "text-[#9a9a9a] dark:text-gray-400",
												)}
											>
												{icon}
											</span>
											{label}
										</Link>
									))}
								</div>
							)}

							<div className="border-t border-(--color-border) pt-3 space-y-0.5">
								<div className="flex items-center justify-between px-3 py-2">
									<span className="text-sm text-(--color-muted-foreground)">
										Theme
									</span>
									<ThemeToggle />
								</div>
								<button
									type="button"
									onClick={handleLogout}
									className="flex items-center gap-3 w-full px-3 py-2.5 text-sm font-medium rounded-lg text-(--color-accent) hover:bg-(--color-accent-muted) transition-colors"
								>
									<LogOut size={15} />
									Sign out
								</button>
							</div>
						</div>
					</nav>
				</>
			)}
		</header>
	);
};

export default Header;
